import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { normalizeDetailedSatisfactionRows, normalizeQueueCountRows, normalizeQueueDistPerDay, normalizeQueueDistPerDayOfWeek, normalizeQueueDistPerHour, normalizeQueuePerformanceRows, normalizeQueueStatusReference, normalizeRatingDistributionRows, normalizeSatisfactionByAgentRows, } from '../model/queue-status.model';
import { appendCriteriaContains, appendCriteriaEquals, appendCriteriaIn, appendPageable, readSpringTotalCount, } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const apiBase = API_ROUTES.qstat.queueReports;
const v2 = API_ROUTES.qstat.queueReportsV2;
function emptySection() {
    return { rows: [], total: 0, loading: false };
}
const SECTION_CONFIG = {
    answeredByAgent: { url: v2.answeredByAgent, includeAgents: true, defaultSize: 10 },
    answeredByQueue: { url: v2.answeredByQueue, includeAgents: false, defaultSize: 10 },
    disconnectionCause: { url: v2.disconnectionCause, includeAgents: false, defaultSize: 10 },
    unansweredByQueue: { url: v2.unansweredByQueue, includeAgents: false, defaultSize: 10 },
    distPerDay: { url: v2.distPerDay, includeAgents: false, defaultSize: 10 },
    distPerHour: { url: v2.distPerHour, includeAgents: true, defaultSize: 10 },
    distPerDayOfWeek: { url: v2.distPerDayOfWeek, includeAgents: false, defaultSize: 7 },
    satisfactionByAgent: { url: v2.satisfactionByAgent, includeAgents: false, defaultSize: 10 },
    ratingDistribution: { url: v2.satisfactionDistribution, includeAgents: false, defaultSize: 10 },
    detailedCallRecords: { url: v2.detailedCallRecords, includeAgents: true, defaultSize: 10 },
    queuePerformance: { url: v2.queuePerformance, includeAgents: false, defaultSize: 10 },
};
function buildLegacyParams(params) {
    const q = new URLSearchParams();
    if (params.startDate)
        q.set('startDate', params.startDate);
    if (params.endDate)
        q.set('endDate', params.endDate);
    if (params.queues?.length)
        q.set('queues', params.queues.join(','));
    if (params.agents?.length)
        q.set('agents', params.agents.join(','));
    return q;
}
function buildV2Params(params, defaults, includeAgents) {
    const q = new URLSearchParams();
    appendPageable(q, params.page ?? defaults.page, params.size ?? defaults.size);
    appendCriteriaEquals(q, 'startDate', params.startDate);
    appendCriteriaEquals(q, 'endDate', params.endDate);
    appendCriteriaIn(q, 'queues', params.queues);
    if (includeAgents && params.agents?.length) {
        if (params.agents.length === 1) {
            appendCriteriaContains(q, 'agents', params.agents[0]);
        }
        else {
            appendCriteriaIn(q, 'agents', params.agents);
        }
    }
    return q;
}
function normalizeSectionRows(section, data) {
    switch (section) {
        case 'answeredByAgent':
        case 'answeredByQueue':
        case 'disconnectionCause':
        case 'unansweredByQueue':
            return normalizeQueueCountRows(data);
        case 'distPerDay':
            return normalizeQueueDistPerDay(data);
        case 'distPerHour':
            return normalizeQueueDistPerHour(data);
        case 'distPerDayOfWeek':
            return normalizeQueueDistPerDayOfWeek(data);
        case 'satisfactionByAgent':
            return normalizeSatisfactionByAgentRows(data);
        case 'ratingDistribution':
            return normalizeRatingDistributionRows(data);
        case 'detailedCallRecords':
            return normalizeDetailedSatisfactionRows(data);
        case 'queuePerformance':
            return normalizeQueuePerformanceRows(data);
        default:
            return [];
    }
}
async function fetchSectionPage(section, params) {
    const config = SECTION_CONFIG[section];
    const q = buildV2Params(params, { page: params.page ?? 0, size: params.size ?? config.defaultSize }, config.includeAgents);
    const res = await axios.get(`${config.url}?${q.toString()}`);
    const rows = normalizeSectionRows(section, res.data);
    const data = res.data;
    const bodyTotal = data && typeof data === 'object' && typeof data.totalElements === 'number'
        ? data.totalElements
        : undefined;
    const total = readSpringTotalCount(res.headers) ?? bodyTotal ?? rows.length;
    return { rows, total };
}
/**
 * Fetch every matching row for CSV export.
 * Uses x-total-count (or totalElements) like incoming/agent productivity reports.
 * If the API caps page size, walks pages until all rows are collected.
 */
export async function fetchQueueReportSectionRows(section, params) {
    const requestedSize = Math.max(params.size ?? 10, 1);
    // Same approach as incoming calls: request page 0 with size = known total.
    const first = await fetchSectionPage(section, { ...params, page: 0, size: requestedSize });
    const total = Math.max(first.total, first.rows.length, 1);
    if (first.rows.length >= total) {
        return { rows: first.rows, total };
    }
    // One-shot with full total (in case caller passed a small size).
    if (requestedSize < total) {
        const full = await fetchSectionPage(section, { ...params, page: 0, size: total });
        if (full.rows.length >= total) {
            return { rows: full.rows, total: Math.max(full.total, full.rows.length) };
        }
    }
    // Backend capped page size — page through until we have all records.
    const chunkSize = Math.max(first.rows.length, 10);
    const allRows = [...first.rows];
    let page = 1;
    while (allRows.length < total) {
        const next = await fetchSectionPage(section, { ...params, page, size: chunkSize });
        if (!next.rows.length)
            break;
        allRows.push(...next.rows);
        if (next.rows.length < chunkSize)
            break;
        page += 1;
        if (page > 500)
            break;
    }
    return { rows: allRows, total };
}
const initialState = {
    loading: false,
    referenceLoading: false,
    detailsLoading: false,
    downloading: false,
    errorMessage: null,
    reference: null,
    answeredByAgent: emptySection(),
    answeredByQueue: emptySection(),
    disconnectionCause: emptySection(),
    unansweredByQueue: emptySection(),
    distPerDay: emptySection(),
    distPerHour: emptySection(),
    distPerDayOfWeek: emptySection(),
    satisfactionByAgent: emptySection(),
    ratingDistribution: emptySection(),
    detailedCallRecords: emptySection(),
    queuePerformance: emptySection(),
    answeredDetails: [],
    unansweredDetails: [],
};
export const getQueueStatusReference = createAsyncThunk('queueStatus/fetch_reference', async () => {
    const [dashboardRes, phoneUsersRes] = await Promise.all([
        axios.get(API_ROUTES.qstat.dashboard),
        axios.get(API_ROUTES.qstat.phoneUsers).catch(() => ({ data: null })),
    ]);
    return normalizeQueueStatusReference(dashboardRes.data, phoneUsersRes.data);
}, { serializeError: serializeAxiosError });
export const fetchQueueReportSection = createAsyncThunk('queueStatus/fetch_section', async ({ section, ...params }) => {
    const result = await fetchSectionPage(section, params);
    return { section, ...result };
}, { serializeError: serializeAxiosError });
export const getAnsweredCallDetails = createAsyncThunk('queueStatus/fetch_answered_details', async (params) => {
    const q = buildLegacyParams(params);
    q.set('action', params.action);
    if (params.agent)
        q.set('agent', params.agent);
    if (params.queue)
        q.set('queue', params.queue);
    return axios.get(`${apiBase}/answered-details?${q.toString()}`);
}, { serializeError: serializeAxiosError });
export const getUnansweredCallDetails = createAsyncThunk('queueStatus/fetch_unanswered_details', async (params) => {
    const q = buildLegacyParams(params);
    q.set('action', params.action);
    if (params.cause)
        q.set('cause', params.cause);
    if (params.queue)
        q.set('queue', params.queue);
    return axios.get(`${apiBase}/unanswered-details?${q.toString()}`);
}, { serializeError: serializeAxiosError });
export const downloadQueueStatusRecording = createAsyncThunk('queueStatus/download', async (fileName) => {
    const q = new URLSearchParams({ fileName });
    return axios.get(`${apiBase}/download?${q.toString()}`, { responseType: 'blob' });
}, { serializeError: serializeAxiosError });
export const queueStatusSlice = createSlice({
    name: 'queueStatus',
    initialState: initialState,
    reducers: {
        clearQueueStatusReport(state) {
            state.errorMessage = null;
            state.loading = false;
            const resetSection = (section) => {
                section.rows = [];
                section.total = 0;
                section.loading = false;
            };
            resetSection(state.answeredByAgent);
            resetSection(state.answeredByQueue);
            resetSection(state.disconnectionCause);
            resetSection(state.unansweredByQueue);
            resetSection(state.distPerDay);
            resetSection(state.distPerHour);
            resetSection(state.distPerDayOfWeek);
            resetSection(state.satisfactionByAgent);
            resetSection(state.ratingDistribution);
            resetSection(state.detailedCallRecords);
            resetSection(state.queuePerformance);
        },
        clearQueueStatusDetails(state) {
            state.answeredDetails = [];
            state.unansweredDetails = [];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getQueueStatusReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload;
        })
            .addCase(fetchQueueReportSection.pending, (state, action) => {
            const section = action.meta.arg.section;
            const current = state[section];
            state[section] = { ...current, loading: true };
            state.loading = true;
            state.errorMessage = null;
        })
            .addCase(fetchQueueReportSection.fulfilled, (state, action) => {
            const { section, rows, total } = action.payload;
            state[section] = {
                rows,
                total,
                loading: false,
            };
            state.loading = false;
        })
            .addCase(fetchQueueReportSection.rejected, (state, action) => {
            const section = action.meta.arg.section;
            const current = state[section];
            state[section] = { ...current, loading: false };
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addCase(getAnsweredCallDetails.fulfilled, (state, action) => {
            state.detailsLoading = false;
            state.answeredDetails = action.payload.data;
        })
            .addCase(getUnansweredCallDetails.fulfilled, (state, action) => {
            state.detailsLoading = false;
            state.unansweredDetails = action.payload.data;
        })
            .addCase(downloadQueueStatusRecording.pending, (state) => {
            state.downloading = true;
        })
            .addCase(downloadQueueStatusRecording.fulfilled, (state) => {
            state.downloading = false;
        })
            .addCase(downloadQueueStatusRecording.rejected, (state) => {
            state.downloading = false;
        })
            .addMatcher(isPending(getQueueStatusReference), (state) => {
            state.referenceLoading = true;
        })
            .addMatcher(isPending(getAnsweredCallDetails, getUnansweredCallDetails), (state) => {
            state.detailsLoading = true;
        })
            .addMatcher(isRejected(getQueueStatusReference), (state, action) => {
            state.referenceLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getAnsweredCallDetails, getUnansweredCallDetails), (state) => {
            state.detailsLoading = false;
        });
    },
});
export const { clearQueueStatusReport, clearQueueStatusDetails } = queueStatusSlice.actions;
export default queueStatusSlice.reducer;
